import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    CardActions,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    Autocomplete,
    Popover,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    Switch,
    FormControlLabel,
    Fab,
    Zoom,
    Checkbox,
    ListItemText,
    OutlinedInput
} from '@mui/material';
import { formatDateTime, formatDate, parseUtcDate } from '../utils/dateHelpers';
import {
    Search as SearchIcon,
    History as HistoryIcon,
    Message as MessageIcon,
    Cancel as CancelIcon,
    SwapHoriz as ReassignIcon,
    FilterList as FilterListIcon,
    RestartAlt as ResetIcon,
    Repeat as RepeatIcon,
    PersonRemove as UnassignIcon,
    PersonAdd as AssignIcon,
    ArrowDropDown as ArrowDropDownIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { ridesAPI, driversAPI, paymentAPI } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import { useCompany } from '../contexts/CompanyContext';
import { safeInvoke } from '../utils/signalRHelper';
import { getRideStatus, getRideStatusColor, getRideStatusStyle } from '../utils/Status';
import DriverMessagingModal from './DriverMessagingModal';
import PaymentForm from './PaymentForm';

const RideHistory = ({ isAdmin = false, onItemClick, initialSearchQuery = '', onSearchQueryUsed }) => {
    const { signalRConnection, connectSignalR } = useAuth();
    const { showAlert, showToast } = useAlert();
    const { settings } = useSettings();
    const { companyCode } = useCompany();
    const pageSize = settings?.defaultPageSize || 50;
    const navigate = useNavigate();
    const location = useLocation();
    const searchInputRef = useRef(null);
    const searchDebounceRef = useRef(null);

    // Get the start of a week (Sunday) for a given date
    const getWeekStartForDate = (date) => {
        const d = new Date(date);
        const dayOfWeek = d.getDay();
        d.setDate(d.getDate() - dayOfWeek);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    // Get initial state from URL
    const getInitialStateFromUrl = () => {
        const params = new URLSearchParams(location.search);
        const mode = params.get('mode') || 'weekly';
        const weekParam = params.get('week');
        const pageParam = params.get('page');
        const searchParam = params.get('search') || initialSearchQuery || '';

        // Check if we have saved filters - if so, force full history mode
        const savedFilters = sessionStorage.getItem('rideHistory_filters');
        const savedApplied = sessionStorage.getItem('rideHistory_filterApplied');
        const hasFilters = savedApplied === 'true' && savedFilters;

        return {
            isFullHistory: hasFilters ? true : mode === 'full',
            weekStart: weekParam ? new Date(weekParam) : getWeekStartForDate(new Date()),
            page: pageParam ? parseInt(pageParam, 10) : 1,
            search: searchParam
        };
    };

    const initialState = getInitialStateFromUrl();

    const [rides, setRides] = useState([]);
    const [filteredRides, setFilteredRides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState(initialState.search);
    const [isFullHistory, setIsFullHistory] = useState(initialState.isFullHistory);
    const [loadingData, setLoadingData] = useState(false);
    const [pendingSearchQuery, setPendingSearchQuery] = useState(initialState.search); // Track initial search to check after load
    const [initialLoadDone, setInitialLoadDone] = useState(false); // Track if initial load is complete
    const [searchNotFound, setSearchNotFound] = useState(null); // Track when a search term is not found

    // Current week being viewed (for weekly mode)
    const [currentWeekStart, setCurrentWeekStart] = useState(initialState.weekStart);

    // Initial page to load (from URL)
    const [initialPage] = useState(initialState.page);

    // Pagination state (used for both weekly and full history modes)
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: pageSize,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    // Messaging state
    const [showMessaging, setShowMessaging] = useState(false);
    const [messagingDriverId, setMessagingDriverId] = useState(null);
    const [messagingDriverName, setMessagingDriverName] = useState('');
    const [messagingRideContext, setMessagingRideContext] = useState(null);

    // Filter state and logic
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterValues, setFilterValues] = useState(() => {
        // Try to restore filters from sessionStorage
        const savedFilters = sessionStorage.getItem('rideHistory_filters');
        if (savedFilters) {
            try {
                return JSON.parse(savedFilters);
            } catch (e) {
                console.error('Error parsing saved filters:', e);
            }
        }
        return {
            dateFrom: '',
            dateTo: '',
            driver: null,
            customerName: '',
            customerNumber: '',
            rideStatus: [],
            paidStatus: [],
            baseFareMin: '',
            baseFareMax: '',
            baseFareRangeMode: false
        };
    });
    const [filterApplied, setFilterApplied] = useState(() => {
        // Check if we had filters applied before
        const savedFilters = sessionStorage.getItem('rideHistory_filters');
        const savedApplied = sessionStorage.getItem('rideHistory_filterApplied');
        return savedApplied === 'true' && savedFilters;
    });
    const [driverOptions, setDriverOptions] = useState([]);
    const [allDriversLoaded, setAllDriversLoaded] = useState(false);

    // Check if any filters are active
    const hasActiveFilters = useCallback(() => {
        return filterValues.dateFrom || filterValues.dateTo || filterValues.driver ||
            filterValues.customerName || filterValues.customerNumber ||
            (filterValues.rideStatus && filterValues.rideStatus.length > 0) ||
            (filterValues.paidStatus && filterValues.paidStatus.length > 0) ||
            filterValues.baseFareMin || filterValues.baseFareMax;
    }, [filterValues]);

    // Track if we need to reload after filter change
    const [filterChangeTriggered, setFilterChangeTriggered] = useState(false);

    // Auto-clear filterApplied when all filters are removed and reload normal data
    useEffect(() => {
        if (filterApplied && !hasActiveFilters()) {
            setFilterApplied(false);
            sessionStorage.removeItem('rideHistory_filterApplied');
            // Reload normal full history when all filters are cleared
            loadFullHistory(1);
        } else if (filterApplied && filterChangeTriggered) {
            // Reload filtered data when a filter is changed (not removed entirely)
            loadFilteredRides(1);
            setFilterChangeTriggered(false);
        }
    }, [filterValues, filterApplied, hasActiveFilters, filterChangeTriggered]);

    // Helper to update a filter and trigger reload
    const updateFilterAndReload = useCallback((filterKey, value) => {
        const newFilters = { ...filterValues, [filterKey]: value };
        setFilterValues(newFilters);
        sessionStorage.setItem('rideHistory_filters', JSON.stringify(newFilters));
        setFilterChangeTriggered(true);
    }, [filterValues]);

    // Assign/Reassign driver menu state
    const [assignMenuAnchorEl, setAssignMenuAnchorEl] = useState(null);
    const [assignMenuRideId, setAssignMenuRideId] = useState(null);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);

    // Paid status toggle state
    const [paidStatusModalOpen, setPaidStatusModalOpen] = useState(false);
    const [paidStatusRide, setPaidStatusRide] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState('');
    const [settingPaidStatus, setSettingPaidStatus] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isFullAmount, setIsFullAmount] = useState(true);
    // Newly-collected card-on-file token (ccof:...) when a dispatcher adds a card at mark-paid time
    const [collectedCardToken, setCollectedCardToken] = useState(null);

    // Dispatcher complete/uncomplete state
    const [dispatcherCompletingId, setDispatcherCompletingId] = useState(null);
    const [dispatcherUncompletingId, setDispatcherUncompletingId] = useState(null);
    // Send Receipt dialog
    const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
    const [receiptRideId, setReceiptRideId] = useState(null);
    const [receiptEmail, setReceiptEmail] = useState('');
    const [receiptSending, setReceiptSending] = useState(false);

    // Scroll to top button state
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Track scroll position to show/hide scroll-to-top button
    useEffect(() => {
        const handleScroll = () => {
            // Show button when scrolled down more than 300px
            setShowScrollTop(window.scrollY > 300);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top handler
    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Focus search input on mount
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Sync search query FROM URL when location changes (e.g., navigating back from detail view)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlSearch = params.get('search') || '';
        if (urlSearch !== searchQuery) {
            setSearchQuery(urlSearch);
            if (urlSearch) {
                setPendingSearchQuery(urlSearch);
            }
        }
    }, [location.search]);

    // Update full URL state (mode, week, page, search)
    const updateUrlState = useCallback((options = {}) => {
        const params = new URLSearchParams();

        const mode = options.mode !== undefined ? options.mode : (isFullHistory ? 'full' : 'weekly');
        const week = options.week !== undefined ? options.week : currentWeekStart;
        const page = options.page !== undefined ? options.page : (pagination?.currentPage || 1);
        const search = options.search !== undefined ? options.search : searchQuery;

        params.set('mode', mode);
        if (mode === 'weekly' && week) {
            params.set('week', week.toISOString().split('T')[0]);
        }
        if (page > 1) {
            params.set('page', page.toString());
        }
        if (search) {
            params.set('search', search);
        }

        const basePath = companyCode ? `/${companyCode}/dispatch` : '/dispatch';
        navigate(`${basePath}/history?${params.toString()}`, { replace: true });
    }, [navigate, companyCode, isFullHistory, currentWeekStart, pagination?.currentPage, searchQuery]);

    // Legacy function for backward compatibility
    const updateUrlWithSearch = useCallback((query) => {
        updateUrlState({ search: query });
    }, [updateUrlState]);

    // Apply initial search query when it changes from prop (e.g., from message links)
    // When searching from external source, switch to full history mode
    useEffect(() => {
        if (initialSearchQuery) {
            setSearchQuery(initialSearchQuery);
            setPendingSearchQuery(initialSearchQuery);
            updateUrlWithSearch(initialSearchQuery);
            // Switch to full history mode for external searches (like message links)
            setIsFullHistory(true);
            loadFullHistory(1);
            // Notify parent that we've used the search query
            if (onSearchQueryUsed) {
                onSearchQueryUsed();
            }
        }
    }, [initialSearchQuery, onSearchQueryUsed, updateUrlWithSearch]);

    // Get the start of this week (Sunday) - uses currentWeekStart state
    const getWeekStart = () => {
        return currentWeekStart;
    };

    // Format the current week for display
    const getWeekDisplayText = () => {
        const endOfWeek = new Date(currentWeekStart);
        endOfWeek.setDate(endOfWeek.getDate() + 6);

        const formatOptions = { month: 'short', day: 'numeric' };
        const startStr = currentWeekStart.toLocaleDateString('en-US', formatOptions);
        const endStr = endOfWeek.toLocaleDateString('en-US', { ...formatOptions, year: 'numeric' });

        // Check if this is the current week
        const now = new Date();
        const thisWeekStart = getWeekStartForDate(now);
        const isCurrentWeek = currentWeekStart.getTime() === thisWeekStart.getTime();

        return isCurrentWeek ? `This Week (${startStr} - ${endStr})` : `${startStr} - ${endStr}`;
    };

    // Navigate to previous week
    const goToPreviousWeek = () => {
        const prevWeek = new Date(currentWeekStart);
        prevWeek.setDate(prevWeek.getDate() - 7);
        setCurrentWeekStart(prevWeek);
        // Clear search and scroll position when changing content
        setSearchQuery('');
        setPendingSearchQuery(null);
        sessionStorage.removeItem('rideHistory_scrollPosition');
        sessionStorage.removeItem('rideHistory_navigatedToDetail');
        // Load the previous week
        loadWeekRides(prevWeek, 1);
    };

    // Navigate to next week
    const goToNextWeek = () => {
        const nextWeek = new Date(currentWeekStart);
        nextWeek.setDate(nextWeek.getDate() + 7);
        setCurrentWeekStart(nextWeek);
        // Clear search and scroll position when changing content
        setSearchQuery('');
        setPendingSearchQuery(null);
        sessionStorage.removeItem('rideHistory_scrollPosition');
        sessionStorage.removeItem('rideHistory_navigatedToDetail');
        // Load the next week
        loadWeekRides(nextWeek, 1);
    };

    // Check if we can go to next week (don't allow future weeks)
    const canGoToNextWeek = () => {
        const thisWeekStart = getWeekStartForDate(new Date());
        return currentWeekStart.getTime() < thisWeekStart.getTime();
    };

    // Save scroll position before navigating to detail view
    const saveScrollPosition = useCallback(() => {
        sessionStorage.setItem('rideHistory_scrollPosition', window.scrollY.toString());
        sessionStorage.setItem('rideHistory_navigatedToDetail', 'true');
    }, []);

    // Restore scroll position after load (only if coming back from detail view)
    const restoreScrollPosition = useCallback(() => {
        const navigatedToDetail = sessionStorage.getItem('rideHistory_navigatedToDetail');
        const savedPosition = sessionStorage.getItem('rideHistory_scrollPosition');

        // Only restore if we actually navigated to detail view and came back
        if (navigatedToDetail === 'true' && savedPosition) {
            setTimeout(() => {
                window.scrollTo({ top: parseInt(savedPosition, 10), behavior: 'instant' });
            }, 100);
        }

        // Clear the navigation flag (we've restored or this is a fresh entry)
        sessionStorage.removeItem('rideHistory_navigatedToDetail');
        sessionStorage.removeItem('rideHistory_scrollPosition');
    }, []);

    // Initial load - use URL state for mode, week, and page, but respect saved filters
    useEffect(() => {
        const loadInitialData = async () => {
            if (initialLoadDone) return;

            // Check if we have saved filters that should be applied
            const savedFilters = sessionStorage.getItem('rideHistory_filters');
            const savedApplied = sessionStorage.getItem('rideHistory_filterApplied');
            const hasFilters = savedApplied === 'true' && savedFilters;

            if (hasFilters) {
                // Load filtered data instead of regular history
                await loadFilteredRides(initialPage);
            } else if (isFullHistory) {
                await loadFullHistory(initialPage);
            } else {
                await loadWeekRides(currentWeekStart, initialPage);
            }

            setInitialLoadDone(true);

            // Restore scroll position after initial load (only if not searching)
            if (!pendingSearchQuery) {
                restoreScrollPosition();
            }
        };

        loadInitialData();
    }, []); // Only run once on mount

    // Update URL when state changes (after initial load)
    useEffect(() => {
        if (initialLoadDone) {
            updateUrlState({
                mode: isFullHistory ? 'full' : 'weekly',
                week: currentWeekStart,
                page: pagination?.currentPage || 1,
                search: searchQuery
            });
        }
    }, [isFullHistory, currentWeekStart, pagination?.currentPage, searchQuery, initialLoadDone, updateUrlState]);

    // After rides load, check if we have a pending search query that wasn't found - if so, find and navigate to correct page
    useEffect(() => {
        const searchForRide = async () => {
            if (!pendingSearchQuery || loading || loadingData) return;

            const query = pendingSearchQuery.toLowerCase().trim();
            const rideId = parseInt(query, 10);

            // Check if ride is in current view
            const foundRide = rides.find(ride => ride.rideId?.toString().toLowerCase() === query);

            if (foundRide) {
                setPendingSearchQuery(null); // Found it, clear pending
                setSearchNotFound(null); // Clear not found state
                // Keep search text visible in the text box
                // Scroll to the found ride after a brief delay
                setTimeout(() => {
                    const element = document.getElementById(`ride-card-${foundRide.rideId}`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Briefly highlight the card
                        element.style.boxShadow = '0 0 10px 3px #1976d2';
                        setTimeout(() => {
                            element.style.boxShadow = '';
                        }, 2000);
                    }
                }, 100);
                return;
            }

            // Ride not found in current view
            if (!isNaN(rideId) && rideId > 0) {
                try {
                    if (isFullHistory) {
                        // Full history mode: find page in full history
                        const result = await ridesAPI.findRidePage(rideId, 50);

                        if (result.found && result.pageNumber > 0) {
                            // Keep search text, just update pending for scroll after load
                            setSearchNotFound(null);
                            setPendingSearchQuery(query);
                            await loadFullHistory(result.pageNumber);
                        } else {
                            setPendingSearchQuery(null);
                            setSearchNotFound(query);
                            showToast(`Ride #${rideId} not found`, 'warning');
                        }
                    } else {
                        // Weekly mode: find which week and page
                        const result = await ridesAPI.findRideWeekAndPage(rideId, 50);

                        if (result.found && result.weekStart && result.pageNumber > 0) {
                            // Keep search text, just update pending for scroll after load
                            setSearchNotFound(null);
                            setPendingSearchQuery(query);
                            const weekStart = new Date(result.weekStart);
                            setCurrentWeekStart(weekStart);
                            await loadWeekRides(weekStart, result.pageNumber);
                        } else {
                            setPendingSearchQuery(null);
                            setSearchNotFound(query);
                            showToast(`Ride #${rideId} not found`, 'warning');
                        }
                    }
                } catch (error) {
                    console.error('Error finding ride:', error);
                    setPendingSearchQuery(null);
                    setSearchNotFound(query);
                    showToast(`Error searching for ride #${rideId}`, 'error');
                }
            } else {
                setPendingSearchQuery(null);
                // Invalid ride ID format - show message if there's actual text
                if (query.length > 0) {
                    setSearchNotFound(query);
                    showToast('Please enter a valid Ride ID (number)', 'warning');
                } else {
                    setSearchNotFound(null);
                }
            }
        };

        searchForRide();
    }, [rides, loading, loadingData, pendingSearchQuery, isFullHistory]);

    // Remove the separate search filter effect - it's now handled in the filter effect above

    // Fetch driver options for filter dropdown (example: from rides)
    useEffect(() => {
        if (!allDriversLoaded) {
            driversAPI.getAll().then(drivers => {
                setDriverOptions(drivers.map(d => ({ label: d.name, id: d.id })));
                setAllDriversLoaded(true);
            });
        }
    }, [allDriversLoaded]);

    // Filter rides based on filterValues
    const getFilteredRidesByFilter = useCallback(() => {
        return rides.filter(ride => {
            let pass = true;
            if (filterValues.dateFrom) {
                pass = pass && parseUtcDate(ride.scheduledFor) >= new Date(filterValues.dateFrom);
            }
            if (filterValues.dateTo) {
                pass = pass && parseUtcDate(ride.scheduledFor) <= new Date(filterValues.dateTo);
            }
            if (filterValues.driver) {
                pass = pass &&
                    ((ride.reassigned && ride.reassignedTo?.name === filterValues.driver.label)
                        || (!ride.reassigned && ride.assignedTo?.name === filterValues.driver.label));
            }
            if (filterValues.customerName) {
                pass = pass && ride.customerName?.toLowerCase().includes(filterValues.customerName.toLowerCase());
            }
            if (filterValues.customerNumber) {
                pass = pass && ride.customerPhoneNumber?.toLowerCase().includes(filterValues.customerNumber.toLowerCase());
            }
            if (filterValues.rideStatus) {
                const rideStatus = getRideStatus(ride);
                pass = pass && rideStatus === filterValues.rideStatus;
            }
            if (filterValues.paidStatus && filterValues.paidStatus.length > 0) {
                const hasPaid = filterValues.paidStatus.includes('paid');
                const hasPartial = filterValues.paidStatus.includes('partial');
                const hasUnpaid = filterValues.paidStatus.includes('unpaid');
                if (hasPaid || hasPartial || hasUnpaid) {
                    pass = pass && (
                        (hasPaid && ride.paid === true) ||
                        (hasPartial && (ride.paidAmount || 0) > 0 && ride.paid !== true) ||
                        (hasUnpaid && !(ride.paidAmount || 0) && ride.paid !== true)
                    );
                }
            }
            if (filterValues.baseFareMin !== '' && filterValues.baseFareMin != null) {
                const baseFare = (ride.cost || 0) - (ride.waitTimeAmount || 0) - (ride.tip || 0);
                pass = pass && baseFare >= parseFloat(filterValues.baseFareMin);
            }
            const effectiveBaseFareMax = filterValues.baseFareRangeMode
                ? filterValues.baseFareMax
                : filterValues.baseFareMin; // exact match: max = min
            if (effectiveBaseFareMax !== '' && effectiveBaseFareMax != null) {
                const baseFare = (ride.cost || 0) - (ride.waitTimeAmount || 0) - (ride.tip || 0);
                pass = pass && baseFare <= parseFloat(effectiveBaseFareMax);
            }
            return pass;
        });
    }, [rides, filterValues]);

    // Load filtered rides from server (paginated)
    const loadFilteredRides = async (page = 1) => {
        try {
            setLoading(true);
            setLoadingData(true);
            setError(null);

            const params = {
                page,
                pageSize: pageSize,
                dateFrom: filterValues.dateFrom || undefined,
                dateTo: filterValues.dateTo || undefined,
                driverId: filterValues.driver?.id || undefined,
                customerName: filterValues.customerName || undefined,
                customerNumber: filterValues.customerNumber || undefined,
                // Join arrays with comma for multi-select filters
                rideStatus: filterValues.rideStatus?.length > 0 ? filterValues.rideStatus.join(',') : undefined,
                paidStatus: filterValues.paidStatus?.length > 0 ? filterValues.paidStatus.join(',') : undefined,
                // Base fare filter (Cost - WaitTimeAmount - Tip)
                baseFareMin: filterValues.baseFareMin !== '' ? filterValues.baseFareMin : undefined,
                baseFareMax: filterValues.baseFareRangeMode
                    ? (filterValues.baseFareMax !== '' ? filterValues.baseFareMax : undefined)
                    : (filterValues.baseFareMin !== '' ? filterValues.baseFareMin : undefined) // exact match
            };

            const data = await ridesAPI.getFilteredRidesPaginated(params);

            setRides(data.rides || []);
            setFilteredRides(data.rides || []);
            setPagination(data.pagination);
            setIsFullHistory(true); // Filtered results are always from full history
        } catch (err) {
            console.error('Error loading filtered rides:', err);
            setError('Failed to load filtered rides. Please try again.');
        } finally {
            setLoading(false);
            setLoadingData(false);
        }
    };

    useEffect(() => {
        const applyFilter = async () => {
            // Skip on initial load - the initial load effect already handles filters
            if (!initialLoadDone) return;

            if (filterApplied && hasActiveFilters()) {
                // Use server-side filtering with pagination
                await loadFilteredRides(1);
            } else if (!filterApplied) {
                // No advanced filter - show all rides
                setFilteredRides(rides);
            }
        }
        applyFilter();
    }, [filterApplied, initialLoadDone]);

    // Load rides for a specific week (paginated)
    const loadWeekRides = async (weekStart, page = 1) => {
        try {
            setLoading(true);
            setLoadingData(true);
            setError(null);
            const data = await ridesAPI.getRidesForWeek(weekStart, page, pageSize);

            setRides(data.rides || []);
            setFilteredRides(data.rides || []);
            setPagination(data.pagination);
            setIsFullHistory(false);
        } catch (err) {
            console.error('Error loading week rides:', err);
            setError('Failed to load ride history. Please try again.');
        } finally {
            setLoading(false);
            setLoadingData(false);
        }
    };

    // Load full history (paginated)
    const loadFullHistory = async (page = 1) => {
        try {
            setLoading(true);
            setLoadingData(true);
            setError(null);
            const data = await ridesAPI.getAllHistoryPaginated(page, pageSize);

            setRides(data.rides || []);
            setFilteredRides(data.rides || []);
            setPagination(data.pagination);
            setIsFullHistory(true);
        } catch (err) {
            console.error('Error loading full history:', err);
            setError('Failed to load full history. Please try again.');
        } finally {
            setLoading(false);
            setLoadingData(false);
        }
    };

    // Handle pagination navigation
    const handlePageChange = (newPage) => {
        // Clear scroll position and navigation flag when changing pages
        sessionStorage.removeItem('rideHistory_scrollPosition');
        sessionStorage.removeItem('rideHistory_navigatedToDetail');

        if (filterApplied && hasActiveFilters()) {
            loadFilteredRides(newPage);
        } else if (isFullHistory) {
            loadFullHistory(newPage);
        } else {
            loadWeekRides(currentWeekStart, newPage);
        }
        // Scroll to top of list
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Toggle between weekly and full history modes
    const handleToggleHistoryMode = async (event) => {
        const newIsFullHistory = event.target.checked;
        setIsFullHistory(newIsFullHistory);
        // Clear scroll position when changing modes
        sessionStorage.removeItem('rideHistory_scrollPosition');
        sessionStorage.removeItem('rideHistory_navigatedToDetail');

        // Preserve search query if present - will trigger search after load
        const currentSearch = searchQuery.trim();
        if (currentSearch) {
            setPendingSearchQuery(currentSearch);
        }

        if (newIsFullHistory) {
            // Switching to full history - check if we have filters to apply
            if (filterApplied && hasActiveFilters()) {
                await loadFilteredRides(1);
            } else {
                await loadFullHistory(1);
            }
        } else {
            // Switching to weekly - clear filters since they don't apply to weekly view
            setFilterApplied(false);
            setFilterValues({ dateFrom: '', dateTo: '', driver: null, customerName: '', customerNumber: '', rideStatus: [], paidStatus: [], baseFareMin: '', baseFareMax: '', baseFareRangeMode: false });
            sessionStorage.removeItem('rideHistory_filters');
            sessionStorage.removeItem('rideHistory_filterApplied');
            // Go back to this week
            const thisWeekStart = getWeekStartForDate(new Date());
            setCurrentWeekStart(thisWeekStart);
            await loadWeekRides(thisWeekStart, 1);
        }
    };

    // Check if ride is completed (has dropOffTime)
    const isRideCompleted = (ride) => {
        return ride.dropOffTime != null;
    };

    const handleCancelCall = async (rideId) => {
        showAlert(
            'Cancel Call',
            'Are you sure you want to cancel this call?',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Confirm',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            const result = await safeInvoke({
                                connection: signalRConnection,
                                connectFn: connectSignalR,
                                methodName: 'CallCanceled',
                                args: [rideId],
                                onRetrying: (attempt, max) => {
                                    showToast(`Retrying... (${attempt}/${max})`, 'info');
                                }
                            });

                            if (!result.success) {
                                showAlert('Connection Error', `Failed to cancel call: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                                return;
                            }

                            showToast('Call cancelled successfully', 'success');
                            // Refresh the data
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                        } catch (error) {
                            console.error('Failed to cancel call:', error);
                            showAlert('Error', `Failed to cancel call: ${error.message}`, [{ text: 'OK' }], 'error');
                        }
                    }
                }
            ],
            'warning'
        );
    };

    // Unassign a driver from a call (does NOT make it available to other drivers)
    const handleUnassignCall = async (rideId) => {
        showAlert(
            'Unassign Driver',
            'Are you sure you want to remove the driver from this call? The call will not be shown to other drivers.',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Unassign',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            const result = await safeInvoke({
                                connection: signalRConnection,
                                connectFn: connectSignalR,
                                methodName: 'UnassignCall',
                                args: [rideId],
                                onRetrying: (attempt, max) => {
                                    showToast(`Retrying... (${attempt}/${max})`, 'info');
                                }
                            });

                            if (!result.success) {
                                showAlert('Connection Error', `The unassign may not have completed: ${result.error}. Please refresh to check.`, [{ text: 'OK' }], 'error');
                                if (isFullHistory) {
                                    loadFullHistory(pagination?.currentPage || 1);
                                } else {
                                    loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                                }
                                return;
                            }

                            showToast('Driver unassigned successfully', 'success');
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                        } catch (error) {
                            console.error('Failed to unassign driver:', error);
                            showAlert('Error', `The unassign may not have completed: ${error.message}. Please refresh to check.`, [{ text: 'OK' }], 'error');
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                        }
                    }
                }
            ],
            'warning'
        );
    };

    // Open the assign/reassign menu for a ride
    const handleOpenAssignMenu = async (event, rideId) => {
        setAssignMenuAnchorEl(event.currentTarget);
        setAssignMenuRideId(rideId);

        // Load available drivers
        setLoadingDrivers(true);
        try {
            const drivers = await driversAPI.getAll();
            setAvailableDrivers(drivers || []);
        } catch (error) {
            console.error('Failed to load drivers:', error);
            showAlert('Error', 'Failed to load drivers list', [{ text: 'OK' }], 'error');
        } finally {
            setLoadingDrivers(false);
        }
    };

    const handleCloseAssignMenu = () => {
        setAssignMenuAnchorEl(null);
        setAssignMenuRideId(null);
    };

    // Get ride info for the assign menu
    const getAssignMenuRide = () => {
        return rides?.find(r => r.rideId === assignMenuRideId);
    };

    // Check if ride is currently assigned
    const isRideAssigned = (ride) => {
        return ride && ((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId));
    };

    // Confirm and assign/reassign a call to a specific driver
    const handleSelectDriver = (driver) => {
        if (!driver) return;

        const ride = getAssignMenuRide();
        const isAssigned = isRideAssigned(ride);
        const actionText = isAssigned ? 'reassign' : 'assign';
        const rideId = assignMenuRideId;

        handleCloseAssignMenu();

        showAlert(
            `Confirm ${isAssigned ? 'Reassignment' : 'Assignment'}`,
            `Are you sure you want to ${actionText} this call to ${driver.name} (#${driver.id})?`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: isAssigned ? 'Reassign' : 'Assign',
                    onPress: async () => {
                        try {
                            const result = await safeInvoke({
                                connection: signalRConnection,
                                connectFn: connectSignalR,
                                methodName: 'DispatcherAssignCall',
                                args: [rideId, driver.id],
                                onRetrying: (attempt, max) => {
                                    showToast(`Retrying... (${attempt}/${max})`, 'info');
                                }
                            });

                            if (!result.success) {
                                showAlert('Connection Error', `Failed to assign call: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                                return;
                            }

                            showToast('Call assigned successfully', 'success');
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                        } catch (error) {
                            console.error('Failed to assign call:', error);
                            showAlert('Error', `Failed to assign call: ${error.message}`, [{ text: 'OK' }], 'error');
                        }
                    }
                }
            ],
            'warning'
        );
    };

    // Cancel recurring series with options
    const handleCancelRecurring = (rideId) => {
        showAlert(
            'Cancel Recurring Series',
            'Do you want to keep this ride or cancel it along with the series?',
            [
                { text: 'Go Back', style: 'cancel' },
                {
                    text: 'Keep This Ride',
                    onPress: async () => {
                        try {
                            await ridesAPI.cancelRecurringWithOption(rideId, true);
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                            showToast('Recurring series canceled. Current ride kept.', 'success');
                        } catch (error) {
                            console.error('Error canceling recurring ride:', error);
                            showAlert('Error', 'Failed to cancel recurring ride. Please try again.', [{ text: 'OK' }], 'error');
                        }
                    }
                },
                {
                    text: 'Cancel Full Series',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await ridesAPI.cancelRecurringWithOption(rideId, false);
                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                            showToast('Recurring series and current ride canceled.', 'success');
                        } catch (error) {
                            console.error('Error canceling recurring ride:', error);
                            showAlert('Error', 'Failed to cancel recurring ride. Please try again.', [{ text: 'OK' }], 'error');
                        }
                    }
                }
            ],
            'warning'
        );
    };

    const handleMessageDriver = (ride) => {
        let targetDriverId = ride.reassigned ? ride.reassignedToId : ride.assignedToId;
        let targetDriverName = ride.reassigned
            ? (ride.reassignedTo?.name || `Driver #${ride.reassignedToId}`)
            : (ride.assignedTo?.name || `Driver #${ride.assignedToId}`);

        let rideContext = {
            rideId: ride.rideId,
            customerName: ride.customerName,
            customerPhone: ride.customerPhoneNumber
        };

        setMessagingDriverId(targetDriverId);
        setMessagingDriverName(targetDriverName || `Driver #${targetDriverId}`);
        setMessagingRideContext(rideContext);
        setShowMessaging(true);
    };

    const handleMessagingClose = () => {
        setShowMessaging(false);
        setMessagingDriverId(null);
        setMessagingDriverName('');
        setMessagingRideContext(null);
    };


    const handleResetPickupTime = async (callId) => {
        showAlert(
            'Reset Pickup Time',
            'Are you sure you want to reset the pickup time for this call? The driver will be notified.',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Reset',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            const result = await safeInvoke({
                                connection: signalRConnection,
                                connectFn: connectSignalR,
                                methodName: 'ResetPickupTime',
                                args: [callId],
                                onRetrying: (attempt, max) => {
                                    showToast(`Retrying... (${attempt}/${max})`, 'info');
                                }
                            });

                            if (!result.success) {
                                showAlert('Connection Error', `Failed to reset pickup time: ${result.error}. Please try again.`, [{ text: 'OK' }], 'error');
                                return;
                            }

                            showToast('Pickup time reset and driver notified', 'success');

                            if (isFullHistory) {
                                loadFullHistory(pagination?.currentPage || 1);
                            } else {
                                loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                            }
                        } catch (error) {
                            console.error('Failed to reset pickup time:', error);
                            showAlert('Error', `Failed to reset pickup time: ${error.message}`, [{ text: 'OK' }], 'error');
                        }
                    }
                }
            ],
            'warning'
        );
    };

    // Open paid status modal
    const handleOpenPaidStatusModal = (ride) => {
        setPaidStatusRide(ride);
        setSelectedPaymentType(ride.paid ? (ride.paymentType || '') : 'cash');
        setCollectedCardToken(null);
        // Pre-fill amount with remaining balance
        const total = (ride.cost || 0) + (ride.tip || 0) + (ride.waitTimeAmount || 0);
        const alreadyPaid = ride.paidAmount || 0;
        const remaining = Math.max(0, total - alreadyPaid);
        setPaymentAmount(remaining.toFixed(2));
        setIsFullAmount(true);
        setPaidStatusModalOpen(true);
    };

    // Dispatcher complete
    const handleDispatcherComplete = (ride) => {
        const isCC = ride.paymentType === 'dispatcherCC' && ride.paymentTokenId && !ride.paid;
        const total = ((ride.cost || 0) + (ride.tip || 0) + (ride.waitTimeAmount || 0)).toFixed(2);
        const msg = isCC
            ? `Card on file will be charged $${total}. Mark ride #${ride.rideId} as complete?`
            : `Mark ride #${ride.rideId} as complete?`;
        showAlert('Mark Complete', msg, [
            {
                text: 'Confirm', onPress: async () => {
                    setDispatcherCompletingId(ride.rideId);
                    let actionSucceeded = false;
                    try {
                        await ridesAPI.dispatcherCompleteRide(ride.rideId);
                        actionSucceeded = true;
                        showToast('Ride marked as complete', 'success');
                    } catch (err) {
                        showAlert('Error', `Failed: ${err.response?.data?.message || err.message}`, [{ text: 'OK' }], 'error');
                    } finally {
                        setDispatcherCompletingId(null);
                    }
                    if (actionSucceeded) {
                        if (filterApplied) loadFilteredRides(pagination?.currentPage || 1);
                        else if (isFullHistory) loadFullHistory(pagination?.currentPage || 1);
                        else loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                    }
                }
            },
            { text: 'Cancel' }
        ]);
    };

    // Dispatcher uncomplete
    const handleDispatcherUncomplete = (ride) => {
        showAlert(
            'Mark Uncompleted',
            `This will revert ride #${ride.rideId} to PickedUp status. Payment status will NOT change.`,
            [
                {
                    text: 'Confirm', onPress: async () => {
                        setDispatcherUncompletingId(ride.rideId);
                        let actionSucceeded = false;
                        try {
                            await ridesAPI.dispatcherUncompleteRide(ride.rideId);
                            actionSucceeded = true;
                            showToast('Ride reverted to PickedUp', 'success');
                        } catch (err) {
                            showAlert('Error', `Failed: ${err.response?.data?.message || err.message}`, [{ text: 'OK' }], 'error');
                        } finally {
                            setDispatcherUncompletingId(null);
                        }
                        if (actionSucceeded) {
                            if (filterApplied) loadFilteredRides(pagination?.currentPage || 1);
                            else if (isFullHistory) loadFullHistory(pagination?.currentPage || 1);
                            else loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
                        }
                    }
                },
                { text: 'Cancel' }
            ]
        );
    };

    // Send Receipt handlers
    const handleOpenReceiptDialog = (rideId) => {
        setReceiptRideId(rideId);
        setReceiptEmail('');
        setReceiptDialogOpen(true);
    };

    const handleSendReceipt = async () => {
        setReceiptSending(true);
        try {
            const result = await ridesAPI.sendReceipt(receiptRideId, receiptEmail || null);
            showToast(`Receipt sent to: ${result.sentTo?.join(', ') || 'recipient'}`, 'success');
            setReceiptDialogOpen(false);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || err.message;
            showAlert('Failed to Send Receipt', typeof msg === 'string' ? msg : 'Please provide an email address.', [{ text: 'OK' }], 'error');
        } finally {
            setReceiptSending(false);
        }
    };

    // Close paid status modal
    const handleClosePaidStatusModal = () => {
        setPaidStatusModalOpen(false);
        setPaidStatusRide(null);
        setSelectedPaymentType('');
        setCollectedCardToken(null);
        setPaymentAmount('');
        setIsFullAmount(true);
    };

    // Handle a card being tokenized in the mark-paid modal (Card on File with no saved card yet)
    const handlePaidModalCardToken = async (nonce, cardholderName) => {
        try {
            const customerName = paidStatusRide?.customerName || cardholderName || 'Unknown';
            const result = await paymentAPI.saveCardOnFile(nonce, customerName);
            if (result.success) {
                setCollectedCardToken(result.cardId);
                showToast(`${result.cardBrand} ending in ${result.last4} ready to charge`, 'success');
            } else {
                showAlert('Error', `Failed to save card: ${result.message}`, [{ text: 'OK' }], 'error');
            }
        } catch (err) {
            console.error('SaveCardOnFile error:', err);
            showAlert('Error', 'Failed to process card. Please try again.', [{ text: 'OK' }], 'error');
        }
    };

    // Confirm paid status change
    const handleConfirmPaidStatus = async () => {
        if (!paidStatusRide) return;

        const newPaidStatus = !paidStatusRide.paid;

        // If marking as paid, require payment type
        if (newPaidStatus && !selectedPaymentType) {
            showToast('Please select a payment method', 'error');
            return;
        }

        // Card on file requires a card to charge (existing token or a newly-collected one)
        const isDispatcherCC = newPaidStatus && selectedPaymentType === 'dispatcherCC';
        const cardToken = collectedCardToken || paidStatusRide.paymentTokenId || null;
        if (isDispatcherCC && !cardToken) {
            showToast('Please enter a credit card to charge', 'error');
            return;
        }

        // Validate amount for non-CC payments
        const parsedAmount = newPaidStatus && !isDispatcherCC ? parseFloat(paymentAmount) : null;
        if (newPaidStatus && !isDispatcherCC) {
            if (!parsedAmount || parsedAmount <= 0) {
                showToast('Please enter a valid payment amount', 'error');
                return;
            }
        }

        setSettingPaidStatus(true);
        try {
            const result = await ridesAPI.setPaidStatus(
                paidStatusRide.rideId,
                newPaidStatus,
                newPaidStatus ? selectedPaymentType : null,
                isDispatcherCC ? cardToken : null,
                !isDispatcherCC && newPaidStatus ? parsedAmount : null
            );

            const isNowFullyPaid = result?.paid ?? newPaidStatus;
            const isPartial = newPaidStatus && !isNowFullyPaid;
            showToast(
                !newPaidStatus
                    ? 'Ride marked as unpaid'
                    : isDispatcherCC
                        ? 'Card charged — ride marked as paid'
                        : isPartial
                            ? `Partial payment of $${parsedAmount?.toFixed(2)} recorded`
                            : 'Ride marked as paid',
                'success'
            );
            handleClosePaidStatusModal();

            // Refresh the data
            if (filterApplied) loadFilteredRides(pagination?.currentPage || 1);
            else if (isFullHistory) loadFullHistory(pagination?.currentPage || 1);
            else loadWeekRides(currentWeekStart, pagination?.currentPage || 1);
        } catch (error) {
            console.error('Failed to update paid status:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to update paid status';
            showAlert('Payment Error', msg, [{ text: 'OK' }], 'error');
        } finally {
            setSettingPaidStatus(false);
        }
    };

    // Handle search query change - update state immediately but debounce the actual search
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        setSearchNotFound(null); // Clear not found state when typing

        // Clear any existing debounce timeout
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        // Debounce the search - wait 500ms after user stops typing
        searchDebounceRef.current = setTimeout(() => {
            updateUrlWithSearch(query);

            // If searching for a ride ID, set pending search to trigger find-and-navigate
            const rideId = parseInt(query.trim(), 10);
            if (!isNaN(rideId) && rideId > 0) {
                setPendingSearchQuery(query.trim());
            } else if (query.trim() === '') {
                // Clear pending search if query is empty
                setPendingSearchQuery(null);
            }
        }, 500);
    };

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const handleViewDetails = (rideId) => {
        // Save scroll position before navigating away
        saveScrollPosition();
        // Save filters to sessionStorage
        sessionStorage.setItem('rideHistory_filters', JSON.stringify(filterValues));
        sessionStorage.setItem('rideHistory_filterApplied', filterApplied.toString());
        if (onItemClick) {
            onItemClick('ride', rideId, { from: 'history', searchQuery });
        }
    };

    const renderRideItem = (ride) => {
        const status = getRideStatus(ride);
        const completed = isRideCompleted(ride);

        return (
            <Card
                key={ride.rideId}
                id={`ride-card-${ride.rideId}`}
                sx={{
                    mb: 2,
                    backgroundColor: ride.canceled ? '#ffebee' : completed ? '#e8f5e9' : 'inherit',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'box-shadow 0.3s ease'
                }}
            >
                {/* Canceled Overlay */}
                {ride.canceled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-15deg)',
                            zIndex: 10,
                            pointerEvents: 'none'
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#d32f2f',
                                fontWeight: 'bold',
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                                letterSpacing: '0.2em',
                                fontStyle: 'italic'
                            }}
                        >
                            Canceled
                        </Typography>
                    </Box>
                )}

                {/* Completed Overlay */}
                {completed && !ride.canceled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-15deg)',
                            zIndex: 10,
                            pointerEvents: 'none'
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#2e7d32',
                                fontWeight: 'bold',
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                                letterSpacing: '0.2em',
                                fontStyle: 'italic'
                            }}
                        >
                            Completed
                        </Typography>
                    </Box>
                )}

                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" color="primary">
                            Ride Id #{ride.rideId}
                        </Typography>
                        <Chip
                            label={status}
                            color={getRideStatusColor(status)}
                            size="small"
                            sx={getRideStatusStyle(status)}
                        />
                    </Box>
                    {/* Customer Ride details - horizontal layout */}
                    <Box display="flex" flexWrap="wrap" gap={3} alignItems="flex-start">
                        {/* Customer/Phone section */}
                        <Box>
                            {ride.customerName && <Typography variant="body2" color="text.secondary">
                                👨‍👩‍👧‍👦 <strong>Customer:</strong> {ride.customerName}
                            </Typography>}
                            {ride.customerPhoneNumber && <Typography variant="body2" color="text.secondary">
                                ☎️ <strong>Phone #:</strong> {ride.customerPhoneNumber}
                            </Typography>}
                        </Box>

                        {/* Driver section */}
                        {((!ride.reassigned && ride.assignedToId) || ride.reassignedToId) && (
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    🚘 <strong>Driver:</strong> #{ride.reassigned ? ride.reassignedToId : ride.assignedToId} - {ride.reassigned ? ride.reassignedTo?.name : ride.assignedTo?.name}
                                </Typography>
                                {(ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber) && (
                                    <Typography variant="body2" color="text.secondary">
                                        ☎️ <strong>Driver Phone #:</strong> {ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Cost and Driver's Compensation - Admin Only */}
                        {isAdmin && (() => {
                            const rideTotal = (ride.cost || 0) + (ride.tip || 0) + (ride.waitTimeAmount || 0);
                            const isEffectivelyPaid = ride.paid || ((ride.paidAmount || 0) > 0 && (ride.paidAmount || 0) >= rideTotal);
                            const isPartial = !isEffectivelyPaid && (ride.paidAmount || 0) > 0;
                            return (
                                <Box>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="body2" color="text.secondary">
                                            💳 <strong>Payment:</strong>
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={
                                                isEffectivelyPaid
                                                    ? (ride.paymentType ? `Paid (${ride.paymentType})` : 'Paid')
                                                    : (isPartial
                                                        ? `Partial ($${(ride.paidAmount || 0).toFixed(2)})`
                                                        : (ride.paymentTokenId ? 'Card on File' : (ride.paymentType || 'Driver Collects')))
                                            }
                                            color={isEffectivelyPaid ? 'success' : isPartial ? 'warning' : 'default'}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        💵 <strong>Cost:</strong> ${ride.cost || 0}
                                    </Typography>
                                    {isPartial && (
                                        <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                            💳 <strong>Paid so far:</strong> ${(ride.paidAmount).toFixed(2)} of ${rideTotal.toFixed(2)}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        💰 <strong>Driver Comp:</strong> ${ride.driversCompensation || 0}
                                    </Typography>
                                </Box>
                            );
                        })()}


                        {/* Times section */}
                        <Box>
                            {ride.callTime && (
                                <Typography variant="body2" color="text.secondary">
                                    📞 <strong>Call Time:</strong> {formatDateTime(ride.callTime)}
                                </Typography>
                            )}
                            {ride.scheduledFor && (
                                <Typography variant="body2" color="text.secondary">
                                    🗓️ <strong>Scheduled:</strong> {formatDateTime(ride.scheduledFor)}
                                </Typography>
                            )}
                        </Box>

                        {/* Pickup/Dropoff status */}
                        <Box>
                            <Typography variant="body2" color={ride.pickupTime ? "success.main" : "error.main"}>
                                <strong>Picked Up?</strong> {ride.pickupTime ? '✅' : '❌'}
                            </Typography>

                            <Typography variant="body2" color={ride.dropOffTime ? "success.main" : "error.main"}>
                                <strong>Dropped Off?</strong> {ride.dropOffTime ? '✅' : '❌'}
                            </Typography>
                        </Box>

                        {/* Canceled indicator */}
                        {ride.canceled && (
                            <Box>
                                <Typography variant="body2" color="error.main">
                                    ❌ <strong>Canceled</strong>
                                </Typography>
                            </Box>
                        )}

                        {/* Recurring indicator */}
                        {ride.isRecurring && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <RepeatIcon fontSize="small" color="primary" />
                                <Typography variant="body2" color="primary.main">
                                    <strong>Recurring Ride</strong>
                                </Typography>
                            </Box>
                        )}

                        {/* Round Trip indicator */}
                        {ride.route?.roundTrip && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2" sx={{ color: '#856404' }}>
                                    🔄 <strong>Round Trip</strong>
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>

                <CardActions sx={{ flexWrap: 'wrap', gap: 1, p: 2, pt: 0 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleViewDetails(ride.rideId)}
                    >
                        View Details
                    </Button>

                    {ride.completed && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={<ReceiptIcon />}
                            onClick={() => handleOpenReceiptDialog(ride.rideId)}
                        >
                            Send Receipt
                        </Button>
                    )}

                    {/* Show Message button for all non-canceled rides that aren't completed */}
                    {(!ride.canceled && !ride.dropOffTime && ((ride.reassigned && ride.reassignedToId) || (!ride.reassigned && ride.assignedToId))) && (<Button
                        variant="outlined"
                        size="small"
                        color='success'
                        startIcon={<MessageIcon />}
                        onClick={() => handleMessageDriver(ride)}
                    >
                        Message Driver
                    </Button>
                    )}

                    {(!ride.canceled && !ride.pickupTime) && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleCancelCall(ride.rideId)}
                        >
                            Cancel
                        </Button>
                    )}

                    {/* Cancel Recurring button - only show for recurring rides */}
                    {ride.isRecurring && !ride.canceled && !ride.dropOffTime && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<RepeatIcon />}
                            onClick={() => handleCancelRecurring(ride.rideId)}
                        >
                            Cancel Recurring Series
                        </Button>
                    )}

                    {/* Unassign button - removes driver without making available */}
                    {(!ride.canceled && ((!ride.reassigned && ride.assignedToId) ||
                        (ride.reassigned && ride.reassignedToId)) && !ride.pickupTime) && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<UnassignIcon />}
                                onClick={() => handleUnassignCall(ride.rideId)}
                            >
                                Unassign
                            </Button>
                        )}

                    {/* Assign/Reassign dropdown - assigns to specific driver */}
                    {(!ride.canceled && !ride.pickupTime) && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            startIcon={((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId)) ? <ReassignIcon /> : <AssignIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e) => handleOpenAssignMenu(e, ride.rideId)}
                        >
                            {((!ride.reassigned && ride.assignedToId) || (ride.reassigned && ride.reassignedToId)) ? 'Reassign' : 'Assign'}
                        </Button>
                    )}

                    {(ride.pickupTime && !ride.dropOffTime) && (

                        <Button
                            variant="outlined"
                            size="small"
                            color='primary'
                            startIcon={<ResetIcon />}
                            onClick={() => handleResetPickupTime(ride.rideId)}
                        >
                            Reset Pickup Time
                        </Button>
                    )}

                    {/* Paid/Unpaid toggle - always available for non-canceled rides */}
                    {!ride.canceled && (() => {
                        const rideTotal = (ride.cost || 0) + (ride.tip || 0) + (ride.waitTimeAmount || 0);
                        const isEffectivelyPaid = ride.paid || ((ride.paidAmount || 0) > 0 && (ride.paidAmount || 0) >= rideTotal);
                        const isPartial = !isEffectivelyPaid && (ride.paidAmount || 0) > 0;
                        return (
                            <Button
                                variant={isEffectivelyPaid || isPartial ? 'contained' : 'outlined'}
                                size="small"
                                color={isEffectivelyPaid ? 'success' : 'warning'}
                                onClick={() => handleOpenPaidStatusModal(ride)}
                            >
                                {isEffectivelyPaid ? '✓ Paid' : isPartial ? '+ Payment' : '$ Mark Paid'}
                            </Button>
                        );
                    })()}

                    {/* Dispatcher complete/uncomplete */}
                    {!ride.canceled && (() => {
                        const s = (ride.status || '').toLowerCase();
                        const canComplete = s === 'pickedup' || s === 'droppedoff' || s.startsWith('atstop');
                        const canUncomplete = s === 'completed';
                        return (
                            <>
                                {canComplete && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="success"
                                        disabled={dispatcherCompletingId === ride.rideId}
                                        onClick={() => handleDispatcherComplete(ride)}
                                    >
                                        {dispatcherCompletingId === ride.rideId ? <CircularProgress size={14} color="inherit" /> : '✅ Complete'}
                                    </Button>
                                )}
                                {canUncomplete && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="warning"
                                        disabled={dispatcherUncompletingId === ride.rideId}
                                        onClick={() => handleDispatcherUncomplete(ride)}
                                    >
                                        {dispatcherUncompletingId === ride.rideId ? <CircularProgress size={14} color="inherit" /> : '↩️ Uncomplete'}
                                    </Button>
                                )}
                            </>
                        );
                    })()}

                </CardActions>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header - Sticky */}
            <Box sx={{
                position: 'sticky',
                top: 70,
                backgroundColor: 'background.paper',
                zIndex: 100,
                pb: 2,
                mx: -2,
                px: 2,
                pt: 2
            }}>
                {/* Title and Mode Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" fontWeight="bold">
                            {isFullHistory ? 'Full Ride History' : 'Ride History'}
                        </Typography>

                        {/* Week Navigation (only in weekly mode) */}
                        {!isFullHistory && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                    size="small"
                                    onClick={goToPreviousWeek}
                                    disabled={loading}
                                    title="Previous Week"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body1" sx={{ minWidth: 180, textAlign: 'center' }}>
                                    {getWeekDisplayText()}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={goToNextWeek}
                                    disabled={loading || !canGoToNextWeek()}
                                    title="Next Week"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* Mode Toggle */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isFullHistory}
                                onChange={handleToggleHistoryMode}
                                color="primary"
                                disabled={loading || loadingData}
                            />
                        }
                        label="Full History"
                        labelPlacement="start"
                    />
                </Box>

                {/* Search Bar */}
                <Box display="flex" alignItems="center">
                    <TextField
                        inputRef={searchInputRef}
                        label="Search"
                        placeholder="Search by Ride ID"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ flex: 1, mr: 2 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={e => {
                            setFilterAnchorEl(e.currentTarget);
                            setFilterOpen(true);
                        }}
                    >
                        Filter
                    </Button>
                </Box>

                {/* Active Filter Chips */}
                {filterApplied && (
                    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {filterValues.dateFrom && (
                            <Chip
                                label={`From: ${filterValues.dateFrom}`}
                                onDelete={() => updateFilterAndReload('dateFrom', '')}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.dateTo && (
                            <Chip
                                label={`To: ${filterValues.dateTo}`}
                                onDelete={() => updateFilterAndReload('dateTo', '')}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.rideStatus?.length > 0 && (
                            <Chip
                                label={`Status: ${filterValues.rideStatus.join(', ')}`}
                                onDelete={() => updateFilterAndReload('rideStatus', [])}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.paidStatus?.length > 0 && (
                            <Chip
                                label={`Paid: ${filterValues.paidStatus.join(', ')}`}
                                onDelete={() => updateFilterAndReload('paidStatus', [])}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.driver && (
                            <Chip
                                label={`Driver: ${filterValues.driver.label}`}
                                onDelete={() => updateFilterAndReload('driver', null)}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.customerName && (
                            <Chip
                                label={`Customer: ${filterValues.customerName}`}
                                onDelete={() => updateFilterAndReload('customerName', '')}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filterValues.customerNumber && (
                            <Chip
                                label={`Phone: ${filterValues.customerNumber}`}
                                onDelete={() => updateFilterAndReload('customerNumber', '')}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Chip
                            label="Clear All"
                            onClick={() => {
                                setFilterApplied(false);
                                setFilterValues({ dateFrom: '', dateTo: '', driver: null, customerName: '', customerNumber: '', rideStatus: [], paidStatus: [] });
                                sessionStorage.removeItem('rideHistory_filters');
                                sessionStorage.removeItem('rideHistory_filterApplied');
                                // Reload normal history
                                loadFullHistory(1);
                            }}
                            size="small"
                            color="error"
                            variant="outlined"
                        />
                    </Box>
                )}
            </Box>

            {/* Error Message */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Ride List */}
                    <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                        {filteredRides.length === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                <Typography color="text.secondary">
                                    {searchQuery ? 'No rides found matching your search' : 'No rides found'}
                                </Typography>
                            </Box>
                        ) : (
                            filteredRides.map((ride) => renderRideItem(ride))
                        )}
                    </Box>

                    {/* Pagination Controls (for both modes when there are multiple pages) */}
                    {pagination && pagination.totalPages > 0 && !filterApplied && (
                        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            {/* Pagination info and controls */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} - {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} rides
                                </Typography>

                                {pagination.totalPages > 1 && (
                                    <Pagination
                                        count={pagination.totalPages}
                                        page={pagination.currentPage}
                                        onChange={(e, page) => handlePageChange(page)}
                                        color="primary"
                                        disabled={loadingData}
                                        showFirstButton
                                        showLastButton
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Filtered results pagination */}
                    {filterApplied && pagination && pagination.totalPages > 0 && (
                        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} - {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} filtered rides
                                </Typography>

                                {pagination.totalPages > 1 && (
                                    <Pagination
                                        count={pagination.totalPages}
                                        page={pagination.currentPage}
                                        onChange={(e, page) => handlePageChange(page)}
                                        color="primary"
                                        disabled={loadingData}
                                        showFirstButton
                                        showLastButton
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Driver Messaging Modal */}
            <DriverMessagingModal
                isOpen={showMessaging}
                onClose={handleMessagingClose}
                driverId={messagingDriverId}
                driverName={messagingDriverName}
                rideContext={messagingRideContext}
                onNavigateToRideHistory={(rideId) => {
                    // Close the modal and search for the ride
                    handleMessagingClose();
                    setSearchQuery(rideId.toString());
                    setPendingSearchQuery(rideId.toString());
                }}
            />

            {/* Filter Popover */}
            <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2, minWidth: 300, maxHeight: '80vh', overflow: 'auto' } }}
            >
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Date From"
                        type="date"
                        value={filterValues.dateFrom}
                        onChange={e => setFilterValues(v => ({ ...v, dateFrom: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Date To"
                        type="date"
                        value={filterValues.dateTo}
                        onChange={e => setFilterValues(v => ({ ...v, dateTo: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                    />
                    {/* Base Fare Filter */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={filterValues.baseFareRangeMode || false}
                                    onChange={e => setFilterValues(v => ({ ...v, baseFareRangeMode: e.target.checked, baseFareMax: e.target.checked ? v.baseFareMax : '' }))}
                                />
                            }
                            label={<Typography variant="body2">Base fare range</Typography>}
                            sx={{ mb: 0.5 }}
                        />
                        {filterValues.baseFareRangeMode ? (
                            <Box display="flex" gap={1}>
                                <TextField
                                    label="Min ($)"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={filterValues.baseFareMin}
                                    onChange={e => setFilterValues(v => ({ ...v, baseFareMin: e.target.value }))}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Max ($)"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={filterValues.baseFareMax}
                                    onChange={e => setFilterValues(v => ({ ...v, baseFareMax: e.target.value }))}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        ) : (
                            <TextField
                                fullWidth
                                label="Base Fare ($)"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={filterValues.baseFareMin}
                                onChange={e => setFilterValues(v => ({ ...v, baseFareMin: e.target.value }))}
                                helperText="Cost minus wait charges &amp; tip"
                                size="small"
                            />
                        )}
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel>Ride Status</InputLabel>
                        <Select
                            multiple
                            value={filterValues.rideStatus || []}
                            label="Ride Status"
                            onChange={e => setFilterValues(v => ({ ...v, rideStatus: e.target.value }))}
                            input={<OutlinedInput label="Ride Status" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            <MenuItem value="open">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('open')} />
                                <ListItemText primary="Open" />
                            </MenuItem>
                            <MenuItem value="scheduled">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('scheduled')} />
                                <ListItemText primary="Scheduled" />
                            </MenuItem>
                            <MenuItem value="arriving">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('arriving')} />
                                <ListItemText primary="Arriving" />
                            </MenuItem>
                            <MenuItem value="atPickup">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('atPickup')} />
                                <ListItemText primary="At Pickup" />
                            </MenuItem>
                            <MenuItem value="inProgress">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('inProgress')} />
                                <ListItemText primary="In Progress" />
                            </MenuItem>
                            <MenuItem value="droppedOff">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('droppedOff')} />
                                <ListItemText primary="Dropped Off" />
                            </MenuItem>
                            <MenuItem value="completed">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('completed')} />
                                <ListItemText primary="Completed" />
                            </MenuItem>
                            <MenuItem value="canceled">
                                <Checkbox checked={(filterValues.rideStatus || []).includes('canceled')} />
                                <ListItemText primary="Canceled" />
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Paid Status</InputLabel>
                        <Select
                            multiple
                            value={filterValues.paidStatus || []}
                            label="Paid Status"
                            onChange={e => setFilterValues(v => ({ ...v, paidStatus: e.target.value }))}
                            input={<OutlinedInput label="Paid Status" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            <MenuItem value="paid">
                                <Checkbox checked={(filterValues.paidStatus || []).includes('paid')} />
                                <ListItemText primary="Paid" />
                            </MenuItem>
                            <MenuItem value="partial">
                                <Checkbox checked={(filterValues.paidStatus || []).includes('partial')} />
                                <ListItemText primary="Partially Paid" />
                            </MenuItem>
                            <MenuItem value="unpaid">
                                <Checkbox checked={(filterValues.paidStatus || []).includes('unpaid')} />
                                <ListItemText primary="Unpaid" />
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        options={driverOptions}
                        value={filterValues.driver}
                        onChange={(_, value) => setFilterValues(v => ({ ...v, driver: value }))}
                        renderInput={params => <TextField {...params} label="Driver" />}
                    />
                    <TextField
                        label="Customer Name"
                        value={filterValues.customerName}
                        onChange={e => setFilterValues(v => ({ ...v, customerName: e.target.value }))}
                    />
                    <TextField
                        label="Customer Number"
                        value={filterValues.customerNumber}
                        onChange={e => setFilterValues(v => ({ ...v, customerNumber: e.target.value }))}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            setFilterApplied(true);
                            setFilterOpen(false);
                        }}
                    >
                        Apply Filter
                    </Button>
                </Box>
            </Popover>

            {/* Assign/Reassign Driver Popover */}
            <Popover
                open={Boolean(assignMenuAnchorEl)}
                anchorEl={assignMenuAnchorEl}
                onClose={handleCloseAssignMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, width: 300 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {isRideAssigned(getAssignMenuRide()) ? 'Reassign to Driver' : 'Assign to Driver'}
                    </Typography>
                    {loadingDrivers ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            <Typography variant="body2">Loading drivers...</Typography>
                        </Box>
                    ) : (
                        <Autocomplete
                            options={availableDrivers}
                            getOptionLabel={(option) => `#${option.id} - ${option.name}`}
                            openOnFocus
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search drivers..."
                                    size="small"
                                    autoFocus
                                />
                            )}
                            onChange={(_, value) => handleSelectDriver(value)}
                            noOptionsText="No drivers found"
                            sx={{ minWidth: 250 }}
                        />
                    )}
                </Box>
            </Popover>

            {/* Paid Status Modal */}
            <Dialog
                open={paidStatusModalOpen}
                onClose={handleClosePaidStatusModal}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {paidStatusRide?.paid ? 'Mark Ride as Unpaid' : (paidStatusRide?.paidAmount > 0 ? 'Add Payment' : 'Mark Ride as Paid')}
                </DialogTitle>
                <DialogContent>
                    {paidStatusRide?.paid ? (
                        <DialogContentText>
                            This ride is currently marked as paid ({paidStatusRide.paymentType || 'unknown'}).
                            Do you want to mark it as unpaid?
                        </DialogContentText>
                    ) : (
                        <>
                            {(() => {
                                const total = ((paidStatusRide?.cost || 0) + (paidStatusRide?.tip || 0) + (paidStatusRide?.waitTimeAmount || 0));
                                const alreadyPaid = paidStatusRide?.paidAmount || 0;
                                const remaining = Math.max(0, total - alreadyPaid);
                                return alreadyPaid > 0 ? (
                                    <DialogContentText sx={{ mb: 1 }}>
                                        Previously paid: <strong>${alreadyPaid.toFixed(2)}</strong> &nbsp;|&nbsp;
                                        Remaining: <strong>${remaining.toFixed(2)}</strong>
                                    </DialogContentText>
                                ) : (
                                    <DialogContentText sx={{ mb: 2 }}>
                                        Total: <strong>${total.toFixed(2)}</strong>
                                    </DialogContentText>
                                );
                            })()}
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="payment-type-label">Payment Method</InputLabel>
                                <Select
                                    labelId="payment-type-label"
                                    value={selectedPaymentType}
                                    label="Payment Method"
                                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                                >
                                    <MenuItem value="cash">💵 Cash</MenuItem>
                                    <MenuItem value="zelle">📱 Zelle</MenuItem>
                                    <MenuItem value="dispatcherCC">💳 Card on File</MenuItem>
                                    <MenuItem value="other">📝 Other</MenuItem>
                                </Select>
                            </FormControl>

                            {selectedPaymentType && selectedPaymentType !== 'dispatcherCC' && (
                                <Box sx={{ mt: 1 }}>
                                    <TextField
                                        fullWidth
                                        label="Amount ($)"
                                        type="number"
                                        inputProps={{ min: 0.01, step: 0.01 }}
                                        value={paymentAmount}
                                        disabled={isFullAmount}
                                        onChange={(e) => {
                                            setPaymentAmount(e.target.value);
                                            const total = ((paidStatusRide?.cost || 0) + (paidStatusRide?.tip || 0) + (paidStatusRide?.waitTimeAmount || 0));
                                            const remaining = Math.max(0, total - (paidStatusRide?.paidAmount || 0));
                                            setIsFullAmount(parseFloat(e.target.value) >= remaining);
                                        }}
                                        size="small"
                                    />
                                    <FormControlLabel
                                        sx={{ mt: 0.5 }}
                                        control={
                                            <Checkbox
                                                checked={isFullAmount}
                                                onChange={(e) => {
                                                    setIsFullAmount(e.target.checked);
                                                    if (e.target.checked) {
                                                        const total = ((paidStatusRide?.cost || 0) + (paidStatusRide?.tip || 0) + (paidStatusRide?.waitTimeAmount || 0));
                                                        const remaining = Math.max(0, total - (paidStatusRide?.paidAmount || 0));
                                                        setPaymentAmount(remaining.toFixed(2));
                                                    }
                                                }}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">Full amount</Typography>}
                                    />
                                </Box>
                            )}

                            {selectedPaymentType === 'dispatcherCC' && (() => {
                                const total = ((paidStatusRide?.cost || 0) + (paidStatusRide?.tip || 0) + (paidStatusRide?.waitTimeAmount || 0));
                                const alreadyPaid = paidStatusRide?.paidAmount || 0;
                                const chargeAmount = Math.max(0, total - alreadyPaid);
                                const hasCard = !!(collectedCardToken || paidStatusRide?.paymentTokenId);
                                return (
                                    <Box sx={{ mt: 1 }}>
                                        {hasCard ? (
                                            <DialogContentText sx={{ color: 'warning.main' }}>
                                                The card on file will be charged <strong>${chargeAmount.toFixed(2)}</strong> (plus 4% CC fee) when you confirm.
                                            </DialogContentText>
                                        ) : (
                                            <>
                                                <DialogContentText sx={{ mb: 1 }}>
                                                    No card on file. Enter a credit card to charge <strong>${chargeAmount.toFixed(2)}</strong> (plus 4% CC fee).
                                                </DialogContentText>
                                                <PaymentForm
                                                    onTokenGenerated={handlePaidModalCardToken}
                                                    onTokenCleared={() => setCollectedCardToken(null)}
                                                    onError={(err) => showAlert('Error', `Failed to process card: ${err}`, [{ text: 'OK' }], 'error')}
                                                />
                                            </>
                                        )}
                                    </Box>
                                );
                            })()}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePaidStatusModal} disabled={settingPaidStatus}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmPaidStatus}
                        variant="contained"
                        color={paidStatusRide?.paid ? "warning" : "success"}
                        disabled={
                            settingPaidStatus
                            || (!paidStatusRide?.paid && !selectedPaymentType)
                            || (!paidStatusRide?.paid && selectedPaymentType !== 'dispatcherCC'
                                && (!paymentAmount || parseFloat(paymentAmount) <= 0))
                            || (!paidStatusRide?.paid && selectedPaymentType === 'dispatcherCC'
                                && !(collectedCardToken || paidStatusRide?.paymentTokenId))
                        }
                    >
                        {settingPaidStatus ? (
                            <CircularProgress size={20} />
                        ) : (
                            paidStatusRide?.paid
                                ? 'Mark Unpaid'
                                : selectedPaymentType === 'dispatcherCC'
                                    ? 'Charge & Mark Paid'
                                    : isFullAmount ? 'Mark Paid' : 'Record Partial Payment'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Receipt Dialog */}
            <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Send Receipt</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Enter an email address to send the receipt to. If the ride has a linked account with an email, it will also receive a copy.
                        Leave blank to send only to the account email (or the company default).
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label="Email Address (optional)"
                        type="email"
                        value={receiptEmail}
                        onChange={e => setReceiptEmail(e.target.value)}
                        placeholder="customer@example.com"
                        helperText="Optional — adds this address as an additional recipient"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiptDialogOpen(false)} disabled={receiptSending}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSendReceipt}
                        disabled={receiptSending}
                        startIcon={receiptSending ? <CircularProgress size={16} /> : <ReceiptIcon />}
                    >
                        Send Receipt
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Scroll to Top Button */}
            <Zoom in={showScrollTop}>
                <Fab
                    color="primary"
                    size="medium"
                    onClick={scrollToTop}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1000
                    }}
                    aria-label="scroll to top"
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </Box>
    );
};

export default RideHistory;
